// A2.2 Activity — every action in this project from all modules (core activity log).
// Filters: module · component · type · person · period (7/30/60/90 days or picked dates) · search.
// Filters combine; "Clear filters" resets them (shown only while a filter is on). Issue codes link to the issue.
// 25 entries per page.
import { useState } from "react";
import { useRpc } from "../api/useRpc";
import { formatDate } from "../format";
import { CodeChip, DateInput, ErrorBox, FilterDropdown, InfoIcon, Loading, PageHeader, SearchBox } from "../ui/kit";
import { ACTIVITY_TYPES, ActivityLine, ActivityTiles, MODULE_NAME } from "../ui/activity";
import type { ActivityItem } from "../api/types";
import { Icon } from "../ui/icons";

interface Filters { module: string; component: string; type: string; person: string; days: string; from: string | null; to: string | null }
export const ACTIVITY_START: Filters = { module: "", component: "", type: "", person: "", days: "30", from: null, to: null };
/** Is any filter different from the default (Past 30 days, everything)? (unit-tested) */
export const activityFiltered = (f: Filters, q: string) => JSON.stringify(f) !== JSON.stringify(ACTIVITY_START) || q.trim() !== "";

export function ActivityPage({ code }: { code: string }) {
  const [v, setV] = useState<Filters>(ACTIVITY_START);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const args: Record<string, unknown> = { code, page, ...(q.trim() ? { q: q.trim() } : {}) };
  for (const k of ["module", "component", "type", "person"] as const) if (v[k]) args[k] = v[k];
  if (v.days === "range") { if (v.from) args.from = v.from; if (v.to) args.to = v.to; } else args.days = Number(v.days);
  const { data, error } = useRpc("project_activity", args);
  const ctx = useRpc("project_context", { code });
  const setF = (patch: Partial<Filters>) => { setV({ ...v, ...patch }); setPage(1); };
  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading />;
  const groups: [string, ActivityItem[]][] = [];
  for (const a of data.items) {
    const day = a.created_at.slice(0, 10);
    if (!groups.length || groups[groups.length - 1][0] !== day) groups.push([day, []]);
    groups[groups.length - 1][1].push(a);
  }
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const periodLabel = v.days === "range" ? `${formatDate(data.from)} – ${formatDate(data.to)}` : `Past ${v.days} days`;
  return (
    <>
      <PageHeader crumb={<>{ctx.data?.project.name} <CodeChip code={code} /> › Activity</>}
        title={<>Activity <InfoIcon tip={["Every action in this project from all modules.", "Filters combine; Clear filters removes them all.", "Click an issue code to open the issue.", "25 entries per page."]} /></>} />
      <div className="filters">
        <FilterDropdown allLabel="All modules" value={v.module} onChange={(x) => setF({ module: x })} options={data.options.modules.map((m) => ({ value: m.id, label: m.name }))} />
        <FilterDropdown allLabel="All components" value={v.component} onChange={(x) => setF({ component: x })} options={data.options.components.map((c) => ({ value: c.prefix, label: `${c.prefix} · ${c.name}` }))} />
        <FilterDropdown allLabel="All types" value={v.type} onChange={(x) => setF({ type: x })} options={ACTIVITY_TYPES} />
        <FilterDropdown allLabel="All people" value={v.person} onChange={(x) => setF({ person: x })} options={data.options.people.map((p) => ({ value: p, label: p }))} />
        <FilterDropdown allLabel="All time" label="Period" value={v.days} onChange={(x) => setF({ days: x || "30" })}
          options={[{ value: "7", label: "Past 7 days" }, { value: "30", label: "Past 30 days" }, { value: "60", label: "Past 60 days" }, { value: "90", label: "Past 90 days" },
                    { value: "range", label: periodLabel.startsWith("Past") ? "Select dates…" : periodLabel, stay: true }]}
          extra={() => v.days === "range" ? (
            <div className="range" onMouseDown={(e) => e.stopPropagation()}>
              <DateInput ariaLabel="From" value={v.from ?? data.from} onChange={(d) => d && setF({ from: d, days: "range" })} />
              <span>–</span>
              <DateInput ariaLabel="To" value={v.to ?? data.to} onChange={(d) => d && setF({ to: d, days: "range" })} />
            </div>) : null} />
        {activityFiltered(v, q) && <button className="btn link accent" onClick={() => { setV(ACTIVITY_START); setQ(""); setPage(1); }}>Clear filters</button>}
        <span className="grow" />
        <SearchBox placeholder="Search activity" value={q} onChange={(x) => { setQ(x); setPage(1); }} width={240} />
      </div>
      <div className="act-tiles four"><ActivityTiles counts={data.counts} /></div>
      <section className="card act-page">
        <div className="act-scroll" data-testid="activity-list">
          {groups.length === 0 && <p className="muted pad">No activity matches these filters.</p>}
          {groups.map(([day, rows]) => (
            <div key={day}>
              <div className="act-day">{formatDate(day)}</div>
              {rows.map((a) => (
                <div key={a.id} className="act-row">
                  <span className="time">{a.created_at.slice(11, 16)}</span>
                  <ActivityLine a={a} wide meta={false} code={code} />
                  <span className="where">{MODULE_NAME[a.module_id] ?? a.module_id}{a.component ? ` · ${a.component}` : ""}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="pager" data-testid="activity-pager">
          <span>Showing {data.total === 0 ? 0 : (data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of {data.total}</span>
          <div className="row gap6">
            <button className="btn" disabled={data.page <= 1} onClick={() => setPage(page - 1)}><Icon name="chevron_left" /> Prev</button>
            <button className="btn" disabled={data.page >= pages} onClick={() => setPage(page + 1)}>Next <Icon name="chevron_right" /></button>
          </div>
        </div>
      </section>
    </>
  );
}
